/**
 * SidebarConfigAdmin — Super admin only
 *
 * Controla qué secciones del sidebar ve el rol admin.
 * - TOGGLE: visible para admin por defecto → super admin puede ocultar
 * - GRANTABLE: solo super admin por defecto → super admin puede conceder
 * - ALWAYS_SUPER_ADMIN: informativo, no modificable
 */

import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { Eye, EyeOff, Loader2, Lock, RotateCcw, Save, Unlock } from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

interface NavItemDef {
  to: string;
  label: string;
  section: string;
}

type InteractiveItem = NavItemDef & { kind: "toggle" | "grant" };

// ─── Item definitions ─────────────────────────────────────────────────────────

const TOGGLE_ITEMS: NavItemDef[] = [
  { to: "/admin/site-settings",    label: "Configuración del sitio", section: "Contenido" },
  { to: "/admin/country-templates",label: "Templates by country",    section: "Contenido" },
  { to: "/admin/portfolio",        label: "Portfolio",               section: "Contenido" },
  { to: "/admin/audiovisual",      label: "Audiovisual",             section: "Contenido" },
  { to: "/admin/reels",            label: "Reels privados",          section: "Contenido" },
  { to: "/admin/productos",        label: "Productos",               section: "Comercio"  },
  { to: "/admin/wishlist-config",  label: "CTA por región",          section: "Comercio"  },
  { to: "/admin/argen-links",      label: "Links Argentina 🇦🇷",      section: "Comercio"  },
];

const GRANTABLE_ITEMS: NavItemDef[] = [
  { to: "/admin/revenue",       label: "Revenue",       section: "Resumen"  },
  { to: "/admin/suscripciones", label: "Suscripciones", section: "Comercio" },
  { to: "/admin/invitaciones",  label: "Invitaciones",  section: "Comercio" },
];

const ALWAYS_SUPER_ADMIN: NavItemDef[] = [
  { to: "/admin/worker-flags",    label: "Worker / Edge",   section: "Sistema" },
  { to: "/admin/integraciones",   label: "Integraciones",   section: "Sistema" },
  { to: "/admin/config-health",   label: "Config Health",   section: "Sistema" },
  { to: "/admin/checkout-funnel", label: "Funnel checkout", section: "Sistema" },
  { to: "/admin/recovery-leads", label: "Leads recovery", section: "Sistema" },
  { to: "/admin/live-funnel",     label: "Live funnel",     section: "Sistema" },
  { to: "/admin/env-setup",       label: "Env vars",        section: "Sistema" },
  { to: "/admin/sidebar-config",  label: "Sidebar",         section: "Sistema" },
];

const ALL_INTERACTIVE: InteractiveItem[] = [
  ...TOGGLE_ITEMS.map((i) => ({ ...i, kind: "toggle" as const })),
  ...GRANTABLE_ITEMS.map((i) => ({ ...i, kind: "grant" as const })),
];

const INTERACTIVE_SECTIONS = [...new Set(ALL_INTERACTIVE.map((i) => i.section))];

// ─── Glass card primitive ─────────────────────────────────────────────────────

function GlassCard({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-2xl border border-white/[0.06] bg-white/[0.03] backdrop-blur-sm", className)}>
      {children}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function SidebarConfigAdmin() {
  const { getAuthHeaders } = useAdminAuth();

  const [hidden,  setHidden]  = useState<Set<string>>(new Set());
  const [granted, setGranted] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res  = await fetch("/api/admin/sidebar-config", { headers });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok) {
          setHidden (new Set<string>(data.config?.hiddenForAdmin  ?? []));
          setGranted(new Set<string>(data.config?.grantedToAdmin ?? []));
        } else {
          setError(data.error ?? "Error cargando configuración");
        }
      } catch {
        if (!cancelled) setError("Error de conexión");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getAuthHeaders]);

  const toggleHidden = (to: string) => {
    setSaved(false);
    setHidden((prev) => { const s = new Set(prev); if (s.has(to)) { s.delete(to); } else { s.add(to); } return s; });
  };
  const toggleGranted = (to: string) => {
    setSaved(false);
    setGranted((prev) => { const s = new Set(prev); if (s.has(to)) { s.delete(to); } else { s.add(to); } return s; });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch("/api/admin/sidebar-config", {
        method: "POST",
        headers,
        body: JSON.stringify({ hiddenForAdmin: [...hidden], grantedToAdmin: [...granted] }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error ?? "Error guardando"); return; }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError("Error de conexión");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSaved(false);
    setHidden(new Set());
    setGranted(new Set());
  };

  const statsLabel = (() => {
    const parts: string[] = [];
    if (hidden.size  > 0) parts.push(`${hidden.size} oculto${hidden.size   > 1 ? "s" : ""}`);
    if (granted.size > 0) parts.push(`${granted.size} concedido${granted.size > 1 ? "s" : ""}`);
    return parts.length === 0 ? "Configuración por defecto" : parts.join(", ");
  })();

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <AdminPageHeader description="Qué ve el rol admin vs super_admin en el menú. Super admin siempre ve todo." />

      {/* Info banner */}
      <GlassCard className="px-5 py-4">
        <p className="text-xs leading-relaxed text-white/40">
          <span className="font-semibold text-white/60">Super admin</span> siempre ve todas las secciones sin importar esta configuración.
          {" "}Items <span className="font-semibold text-white/55">visibles</span> pueden ocultarse; items{" "}
          <span className="font-semibold text-white/55">restringidos</span> pueden concederse al admin.
        </p>
      </GlassCard>

      {loading ? (
        <GlassCard className="flex items-center justify-center gap-3 py-16 text-white/25">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Cargando configuración…</span>
        </GlassCard>
      ) : (
        <div className="space-y-4">
          {/* ── Interactive sections ── */}
          {INTERACTIVE_SECTIONS.map((section) => {
            const items = ALL_INTERACTIVE.filter((i) => i.section === section);
            return (
              <GlassCard key={section} className="overflow-hidden">
                <div className="border-b border-white/[0.05] px-5 py-3">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-white/25">
                    {section}
                  </p>
                </div>
                <div className="divide-y divide-white/[0.04]">
                  {items.map((item) => {
                    if (item.kind === "toggle") {
                      const visible = !hidden.has(item.to);
                      return (
                        <button
                          key={item.to}
                          type="button"
                          onClick={() => toggleHidden(item.to)}
                          className={cn(
                            "group flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left transition-all",
                            visible
                              ? "hover:bg-white/[0.04]"
                              : "hover:bg-white/[0.02]",
                          )}
                        >
                          <div className="flex min-w-0 items-center gap-3">
                            <div className={cn(
                              "flex h-7 w-7 shrink-0 items-center justify-center rounded-xl border transition-all",
                              visible
                                ? "border-white/10 bg-white/[0.06]"
                                : "border-white/[0.04] bg-white/[0.02]",
                            )}>
                              {visible
                                ? <Eye    className="h-3.5 w-3.5 text-white/60" />
                                : <EyeOff className="h-3.5 w-3.5 text-white/20" />}
                            </div>
                            <div className="min-w-0">
                              <p className={cn("text-sm font-medium truncate", visible ? "text-white/70" : "text-white/25")}>
                                {item.label}
                              </p>
                              <p className="hidden text-[10px] text-white/15 sm:block">{item.to}</p>
                            </div>
                          </div>
                          <span className={cn(
                            "shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-all",
                            visible
                              ? "border-white/10 bg-white/[0.06] text-white/50"
                              : "border-white/[0.04] bg-white/[0.02] text-white/20",
                          )}>
                            {visible ? "Visible" : "Oculto"}
                          </span>
                        </button>
                      );
                    }

                    // kind === "grant"
                    const isGranted = granted.has(item.to);
                    return (
                      <button
                        key={item.to}
                        type="button"
                        onClick={() => toggleGranted(item.to)}
                        className="group flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left transition-all hover:bg-white/[0.03]"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div className={cn(
                            "flex h-7 w-7 shrink-0 items-center justify-center rounded-xl border transition-all",
                            isGranted
                              ? "border-white/10 bg-white/[0.06]"
                              : "border-white/[0.04] bg-white/[0.02]",
                          )}>
                            {isGranted
                              ? <Unlock className="h-3.5 w-3.5 text-white/60" />
                              : <Lock   className="h-3.5 w-3.5 text-white/20" />}
                          </div>
                          <div className="min-w-0">
                            <p className={cn("text-sm font-medium truncate", isGranted ? "text-white/70" : "text-white/25")}>
                              {item.label}
                            </p>
                            <p className="hidden text-[10px] text-white/15 sm:block">
                              {item.to}
                              <span className="ml-2 italic text-white/10">solo super admin por defecto</span>
                            </p>
                          </div>
                        </div>
                        <span className={cn(
                          "shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-all",
                          isGranted
                            ? "border-white/10 bg-white/[0.06] text-white/50"
                            : "border-white/[0.04] bg-white/[0.02] text-white/20",
                        )}>
                          {isGranted ? "Concedido" : "Restringido"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </GlassCard>
            );
          })}

          {/* ── Sistema — siempre super admin ── */}
          <GlassCard className="overflow-hidden opacity-50">
            <div className="border-b border-white/[0.05] px-5 py-3">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-white/25">
                Sistema
                <span className="ml-2 normal-case font-normal text-white/15">— siempre requieren super admin</span>
              </p>
            </div>
            <div className="divide-y divide-white/[0.04]">
              {ALWAYS_SUPER_ADMIN.map((item) => (
                <div
                  key={item.to}
                  className="flex cursor-not-allowed items-center justify-between gap-4 px-5 py-3.5"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl border border-white/[0.04] bg-white/[0.02]">
                      <Lock className="h-3.5 w-3.5 text-white/15" />
                    </div>
                    <p className="truncate text-sm font-medium text-white/20">{item.label}</p>
                  </div>
                  <span className="shrink-0 rounded-full border border-white/[0.04] bg-white/[0.02] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/15">
                    Super admin
                  </span>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>
      )}

      {/* Error */}
      {error && (
        <GlassCard className="border-red-500/20 bg-red-500/5 px-5 py-4">
          <p className="text-sm text-red-400/70">{error}</p>
        </GlassCard>
      )}

      {/* Actions */}
      {!loading && (
        <GlassCard className="flex flex-wrap items-center gap-3 px-5 py-4">
          <button
            type="button"
            onClick={handleReset}
            className="flex items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-2 text-sm font-medium text-white/40 transition-all hover:bg-white/[0.07] hover:text-white/70 active:scale-95"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Restaurar todo
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-xl bg-white px-5 py-2 text-sm font-semibold text-black shadow-lg shadow-white/10 transition-all hover:bg-white/90 active:scale-95 disabled:pointer-events-none disabled:opacity-40"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            {saving ? "Guardando…" : "Guardar cambios"}
          </button>
          {saved && (
            <span className="flex items-center gap-1.5 text-sm text-white/40">
              <span className="h-1.5 w-1.5 rounded-full bg-white/40" />
              Guardado
            </span>
          )}
          <span className="ml-auto text-xs text-white/20">{statsLabel}</span>
        </GlassCard>
      )}
    </div>
  );
}
